/**
 * threadTitleAuthority — who gets to name a shared thread.
 *
 * Reported 2026-08-05: Fernando and Malaika sat in the same shared thread and
 * saw two different names for it ("Chat Response Test" vs "Hello Test
 * Exchange"). Nothing was corrupt — the LLM thread labeler runs INDEPENDENTLY
 * in each participant's browser, writes straight to that browser's local thread
 * record, and never publishes to the canonical title on the membership doc. Two
 * clients, two inferences, two names for one conversation.
 *
 * The server already has the correct machinery: a canonical title saved on the
 * membership doc propagates into every participant's thread index — but ONLY
 * when the thread's creator publishes it (`propagateTitleToParticipantIndexes`
 * is gated on `after.createdBy === creatorId`). So the rule this module encodes
 * is the one the server already enforces:
 *
 *   • the CREATOR is the naming authority — their auto-inferred title is
 *     published as canonical and fans out to everyone;
 *   • a PARTICIPANT adopts the canonical and never lets its local labeler
 *     overwrite it — otherwise each side clobbers what the server just
 *     propagated and the two names oscillate forever;
 *   • a SOLO thread is unaffected: local labeling, no publishing.
 *
 * An explicit user rename is unchanged and still wins locally (`userRenamed`);
 * this governs only the automatic labeler.
 *
 * THE BACKSTOP (Miles, 2026-09-12): "first one there names it, assuming they
 * have the right permissions". The creator-only rule above left one hole with
 * no floor under it — the creator can close their tab before their labeler ever
 * resolves, the label fires once per message fingerprint and nothing retried,
 * so a shared thread could read "new thread" to everybody forever. So a
 * participant may publish too, under two conditions that keep the original
 * problem fixed: only while the thread has NO canonical name yet, and only if
 * the thread lets them manage it. Once a name exists the old rule is back
 * exactly as it was — participants adopt, never overwrite. The server settles
 * the race inside its own transaction (first write wins, no flapping).
 *
 * Pure + dependency-free so the rule is unit-tested without React or Firestore.
 */

/** Minimal shape read from a membership row. */
export interface SharedThreadOwnership {
  /** uid that created the thread — the naming authority. */
  createdBy?: string | null;
}

export type ThreadTitleAuthority =
  /** Not a shared thread — label locally, publish nothing. */
  | 'solo'
  /** Shared and self created it — self names it for everyone. */
  | 'owner'
  /** Shared, someone else created it — adopt the canonical, never overwrite. */
  | 'participant';

function trim(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Decide this user's authority over a thread's title.
 *
 * Fail-safe toward 'participant' for a shared thread with unknown ownership:
 * publishing a title we have no claim to is the harmful direction (it would
 * rename the thread under everyone else), whereas declining to publish merely
 * leaves the name as it is.
 */
export function resolveThreadTitleAuthority(args: {
  threadId: string | null | undefined;
  selfId: string | null | undefined;
  /** threadId → ownership, from the membership snapshot. Absent ⇒ not shared. */
  sharedThreads?: Readonly<Record<string, SharedThreadOwnership>> | null;
}): ThreadTitleAuthority {
  const threadId = trim(args.threadId);
  const selfId = trim(args.selfId);
  if (!threadId) return 'solo';

  const shared = args.sharedThreads ?? {};
  if (!Object.prototype.hasOwnProperty.call(shared, threadId)) return 'solo';

  const createdBy = trim(shared[threadId]?.createdBy);
  if (!selfId || !createdBy) return 'participant';
  return createdBy === selfId ? 'owner' : 'participant';
}

/**
 * Should the local automatic labeler be prevented from rewriting this thread's
 * title? True for a shared thread this user did not create — their name for it
 * is not theirs to choose, and letting the labeler write would fight the
 * canonical title the server propagates.
 *
 * The exception is the backstop: while the thread has NO canonical name, there
 * is nothing to fight and nothing to adopt, so a participant labels locally
 * like anyone else (and publishes, if `shouldPublishCanonicalTitle` agrees).
 * `canonicalExists` ABSENT reads as true — the caller that cannot tell keeps
 * the stricter, pre-2026-09-13 behaviour.
 */
export function isAutoTitleSuppressed(
  authority: ThreadTitleAuthority,
  opts?: { canonicalExists?: boolean },
): boolean {
  if (authority !== 'participant') return false;
  return opts?.canonicalExists !== false;
}

/**
 * Should a locally-inferred title be published as the canonical title for all
 * participants? The creator of a shared thread always; a participant only as
 * the backstop — the thread has no canonical name AND it lets them manage it
 * (`threads.manage`, or they are the workspace owner/admin). Either way, only
 * for a title worth propagating (a blank or default one is never published —
 * the fallbacks are per-client presentation, not a name anyone chose).
 *
 * Both backstop inputs ABSENT ⇒ creator-only, the pre-2026-09-13 rule, so a
 * caller that has not been taught the backstop cannot accidentally widen it.
 * The server re-checks both halves; this only decides whether to ask.
 */
export function shouldPublishCanonicalTitle(
  authority: ThreadTitleAuthority,
  title: string | null | undefined,
  opts?: { canonicalExists?: boolean; canManage?: boolean },
): boolean {
  if (!trim(title)) return false;
  if (authority === 'owner') return true;
  if (authority !== 'participant') return false;
  return opts?.canonicalExists === false && opts?.canManage === true;
}
